import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Calendar as CalendarIcon } from "lucide-react";
import { ro } from "date-fns/locale";
import { cn } from "@/lib/utils";

/**
 * DateTimeInput — Romanian zz/ll/an HH:mm picker (date popover + native time spinner).
 * value: "YYYY-MM-DDTHH:mm" (local, no tz) or empty
 * onChange(v): emits "YYYY-MM-DDTHH:mm" local string
 *
 * We split into a date popover (forces dd/MM/yyyy) and a small text-like time
 * input <input type="time"> which is HH:mm everywhere (24h, locale-agnostic).
 */
export default function DateTimeInput({
  value,
  onChange,
  className = "",
  testid,
  disabled,
}) {
  const [open, setOpen] = useState(false);
  const { datePart, timePart } = splitLocal(value);
  const parsedDate = datePart ? parseYMD(datePart) : null;
  const displayDate = parsedDate ? formatDDMMYYYY(parsedDate) : "";

  function emit(d, t) {
    const ymd = d || datePart || todayYMD();
    const hm = t || timePart || "09:00";
    onChange?.(`${ymd}T${hm}`);
  }

  function handleSelect(d) {
    if (!d) return;
    emit(toYMD(d), timePart);
    setOpen(false);
  }

  function handleTime(e) {
    emit(datePart, e.target.value || "00:00");
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-testid={testid}
            className={cn(
              "flex-1 inline-flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-left focus:outline-none focus:ring-2 focus:ring-[#0047FF] disabled:opacity-50",
              !displayDate && "text-slate-400",
            )}
          >
            <CalendarIcon className="w-4 h-4 text-slate-400 shrink-0" />
            <span className="truncate tabular">{displayDate || "zz/ll/an"}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={parsedDate || undefined}
            onSelect={handleSelect}
            locale={ro}
            weekStartsOn={1}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      <input
        type="time"
        data-testid={testid ? `${testid}-time` : undefined}
        value={timePart}
        onChange={handleTime}
        disabled={disabled}
        className="w-[100px] border border-slate-200 rounded-lg px-2 py-2 text-sm tabular focus:outline-none focus:ring-2 focus:ring-[#0047FF] disabled:opacity-50"
      />
    </div>
  );
}

function pad(n) { return String(n).padStart(2, "0"); }

function splitLocal(v) {
  if (!v) return { datePart: "", timePart: "" };
  // Accept "YYYY-MM-DDTHH:mm" / "YYYY-MM-DDTHH:mm:ss" / full ISO with Z or offset
  const isLocal = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v);
  const isFullIso = /Z$|[+-]\d{2}:?\d{2}$/.test(v);
  if (isLocal && !isFullIso) {
    const [d, t] = v.split("T");
    return { datePart: d, timePart: (t || "").slice(0, 5) };
  }
  const d = new Date(v);
  if (isNaN(d.getTime())) return { datePart: "", timePart: "" };
  return {
    datePart: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    timePart: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

function parseYMD(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? null : d;
}

function toYMD(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function todayYMD() {
  return toYMD(new Date());
}

function formatDDMMYYYY(d) {
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}
